import React, { Fragment, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import _ from 'lodash'
import { Controller, useFormContext } from 'react-hook-form'
import Select from 'react-select'

import cx from 'classnames'
import FlowSelect from './FlowSelect'
import Step from './Step'
import TransferToDepartment from './TransferToDepartment'
import PrechatForm from './PrechatForm'
import Link from './Link'
import CalendlyScheduleEvent from './CalendlyScheduleEvent'

import { getAccountFeatures, getApps } from 'Selectors/account'
import FollowUpMessage from './FollowupMessage'

const allowedActionsForBotWidget = [
  'message',
  'quickReplies',
  'guide',
  'answer',
  'botCollectInformation',
  'hfWorkflow',
  'mlSearch',
  'endChat'
]

const componentMap = {
  goToFlow: FlowSelect,
  goToStep: Step,
  link: Link,
  calendly_schedule_event: CalendlyScheduleEvent,
  happyfoxchat_transfer_to_department: TransferToDepartment,
  happyfoxchat_contact_details_form: PrechatForm,
  endChat: FollowUpMessage,
  default: () => null
}

const ActionSelect = (props) => {
  const {
    name = 'action',
    isQuickReplay = false,
    className,
    actionGroup = '',
    allowStepCreate = true,
    showTypeSelector = true,
    allowedActions = [],
    labelOptions
  } = props

  const accountFeatures = useSelector(getAccountFeatures)
  const apps = useSelector(getApps)
  const {
    watch,
    getValues,
    formState: { errors },
    control
  } = useFormContext()

  const actionFormPath = `${name}${actionGroup ? '.' + actionGroup : ''}`
  const actionTypeFormPath = actionFormPath.includes('.type') ? actionFormPath : `${actionFormPath}.type`
  const actionType = watch(actionTypeFormPath, getValues(actionTypeFormPath))

  const ActionComponent = componentMap[actionType] || componentMap.default
  const is_bot_widget = accountFeatures?.bot_widget ?? false

  const isActionDisabled = useCallback(
    (act) => {
      if (act.hidden) {
        return false
      }

      if (typeof act.integrationKey === 'undefined') {
        return true
      }

      if (!act.integrationKey) {
        return false
      }

      return apps.some((app) => app.app_type === act.integrationKey && app.is_active)
    },
    [apps]
  )

  const ALL_ACTIONS = useMemo(() => {
    return [
      {
        name: 'Link to New Step',
        key: 'goToNewStep'
      },
      {
        name: 'End Chat',
        key: 'endChat'
      },
      {
        name: !isQuickReplay ? 'Link to Step' : 'Link to Existing Step',
        key: 'goToStep'
      },
      {
        name: 'Go to Bot',
        key: 'goToFlow',
        featureKey: 'multiflow'
      },
      {
        name: 'Submit',
        key: 'submit',
        hidden: true
      },
      {
        name: 'Open Link in New Tab',
        key: 'link',
        optionFlag: 'openLinkInNewTab'
      },
      {
        name: 'Schedule Event (Calendly)',
        key: 'calendly_schedule_event',
        optionFlag: 'calendly',
        integrationKey: 'calendly'
      },
      {
        name: 'Show Prechat Form',
        key: 'happyfoxchat_contact_details_form',
        optionFlag: 'hfPrechatForm'
      },
      {
        name: 'Transfer Chat to Department',
        key: 'happyfoxchat_transfer_to_department',
        optionFlag: 'transferChatFromQuickReply',
        integrationKey: 'happyfox_chat'
      },
      {
        name: 'Hide the Transfer Option',
        key: 'hide_no_agent_avl_action',
        optionFlag: 'transferChatFromQuickReply',
        relatedTo: ['happyfoxchat_transfer_to_department']
      }
    ]
  }, [isQuickReplay])

  const allActions = useMemo(() => {
    const actions = ALL_ACTIONS.filter((act) => {
      let isActionAllowed = false
      if (accountFeatures[act.featureKey] === false) {
        return false
      }

      const optionsFlags = _.get(accountFeatures, 'flowBuilder.options')

      // If bot_widget is true, consider allowedActionsForBotWidget along with account level feature flag if any
      if (is_bot_widget) {
        isActionAllowed = optionsFlags?.[act.optionFlag] ?? allowedActionsForBotWidget.includes(act.optionFlag)
      } else {
        // If bot_widget is false or undefined, consider account level feature flag if any
        isActionAllowed = optionsFlags?.[act.optionFlag] ?? true
      }

      if (!act.optionFlag) {
        isActionAllowed = true
      }

      if (allowedActions.length !== 0) {
        isActionAllowed = allowedActions.includes(act.key)
      }

      if (isActionAllowed && act.integrationKey && actionType !== act.key) {
        isActionAllowed = apps.some((app) => app.app_type === act.integrationKey && app.is_active)
        return isActionAllowed
      }

      if (act.relatedTo && Array.isArray(act.relatedTo)) {
        const parentActionTypePath = actionFormPath.substring(0, actionFormPath.indexOf(actionGroup)) + 'type'
        const parentActionType = getValues(parentActionTypePath)

        if (!act.relatedTo.includes(parentActionType)) {
          isActionAllowed = false
        }
      }

      return isActionAllowed
    })

    return actions
  }, [
    ALL_ACTIONS,
    accountFeatures,
    is_bot_widget,
    allowedActions,
    actionType,
    apps,
    actionFormPath,
    actionGroup,
    getValues
  ])

  const actionSelectError = _.get(errors, actionTypeFormPath)

  const actionSelectOptions = useMemo(() => {
    return allActions.map((a) => {
      return {
        value: a.key,
        label: a.name,
        hidden: Boolean(a.hidden && actionType !== a.key),
        disabled: !isActionDisabled(a)
      }
    })
  }, [allActions, actionType, isActionDisabled])

  return (
    <div className='action'>
      {showTypeSelector && (
        <Fragment>
          <Controller
            control={control}
            shouldUnregister
            name={actionTypeFormPath}
            rules={{
              required: {
                value: true,
                message: 'Action is required'
              }
            }}
            render={({ field: { value, onChange }, formState: { errors } }) => {
              return (
                <Fragment>
                  {labelOptions && (
                    <label className='form-label' htmlFor='action'>
                      {labelOptions?.name}
                    </label>
                  )}
                  <Select
                    className={cx('mb-3', { 'is-invalid': actionSelectError })}
                    menuPosition='fixed'
                    classNamePrefix={`${className} action`}
                    options={actionSelectOptions.filter((value) => !value.hidden)}
                    menuPortalTarget={document.body}
                    isOptionDisabled={(option) => option.disabled}
                    value={actionSelectOptions.find((i) => i.value === value)}
                    onChange={(option) => {
                      onChange(option.value)
                    }}
                  />
                </Fragment>
              )
            }}
          />
          {actionSelectError && <small className='ml-2 text-danger'>{actionSelectError.message}</small>}
        </Fragment>
      )}
      {actionType && actionType.type !== 'endChat' && (
        <ActionComponent
          name={name}
          className={className}
          allowStepCreate={isQuickReplay ? false : allowStepCreate}
          actionGroup={actionGroup}
        />
      )}
      {actionType && actionType.type === 'endChat' && (
        <FollowUpMessage name={name} allowStepCreate={allowStepCreate} actionGroup={actionGroup} />
      )}
    </div>
  )
}

export default ActionSelect
